import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import {
  CalendarSearch,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  CalendarPlus,
  Link as LinkIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const API = window.location.origin + "/api";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Nasconde il pannello se GCal non è ancora collegato — mostra un CTA
// di collegamento invece.
export default function GCalRecoveryCard() {
  const [gcalStatus, setGcalStatus] = useState(null); // { connected, updated_at }
  const [date, setDate] = useState(todayIso());
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [calendars, setCalendars] = useState([]);
  const [calendarId, setCalendarId] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    // Verifica se GCal è collegato
    axios
      .get(`${API}/google-calendar/status`, { withCredentials: true })
      .then((r) => setGcalStatus(r.data))
      .catch(() => setGcalStatus({ connected: false }));
  }, []);

  const handleLoad = useCallback(async () => {
    if (!date) {
      toast.error("Seleziona una data");
      return;
    }
    setLoading(true);
    setEvents(null);
    setSelected(new Set());
    setResult(null);
    try {
      const { data } = await axios.get(
        `${API}/google-calendar/fetch-day?date=${date}`,
        { withCredentials: true },
      );
      setEvents(data.events || []);
      // Preseleziona tutti quelli con match esatto
      const auto = new Set();
      (data.events || []).forEach((ev) => {
        if (ev.match?.contact_id && ev.match?.match_type !== "ambiguous") {
          auto.add(ev.gcal_event_id);
        }
      });
      setSelected(auto);
      toast.success(`Caricati ${data.total} eventi da Google Calendar`);
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || "Errore lettura GCal";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [date]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedEvents = (events || []).filter((e) =>
    selected.has(e.gcal_event_id),
  );

  const selectedRestorable = selectedEvents.filter(
    (e) => e.match?.contact_id,
  );

  const handleRestore = useCallback(async () => {
    if (!password) {
      toast.error("Inserisci la password master");
      return;
    }
    if (!calendarId) {
      toast.error("Seleziona il calendario di destinazione");
      return;
    }
    if (selectedRestorable.length === 0) {
      toast.error("Nessun evento selezionato con contactId valido");
      return;
    }
    setRestoring(true);
    setResult(null);
    try {
      const payload = {
        password,
        calendar_id: calendarId,
        events: selectedRestorable.map((e) => ({
          contact_id: e.match.contact_id,
          title: e.title,
          start_iso: e.start_iso,
          end_iso: e.end_iso,
          notes: e.description || "",
        })),
      };
      const { data } = await axios.post(
        `${API}/google-calendar/restore-to-ghl`,
        payload,
        { withCredentials: true },
      );
      setResult(data);
      toast.success(
        `Ripristinati ${data.created}/${data.total} appuntamenti in GHL`,
      );
      setConfirmOpen(false);
      setPassword("");
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || "Errore ripristino";
      toast.error(msg);
    } finally {
      setRestoring(false);
    }
  }, [password, calendarId, selectedRestorable]);

  // Se GCal non è collegato → mostra CTA per collegarlo
  if (gcalStatus && !gcalStatus.connected) {
    return (
      <div
        className="bg-white rounded-lg shadow-sm border border-slate-200 p-5"
        data-testid="gcal-recovery-card-not-connected"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-50 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-800">
              Recupero d'emergenza da Google Calendar
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Google Calendar non è collegato all'app. Collegalo per abilitare
              il recupero degli appuntamenti cancellati.
            </p>
            <a
              href={`${API}/google-calendar/auth`}
              className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md"
              data-testid="gcal-recovery-connect-btn"
            >
              <LinkIcon className="w-4 h-4" />
              Collega Google Calendar
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-lg shadow-sm border border-slate-200 p-5"
      data-testid="gcal-recovery-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-emerald-50 rounded-lg">
          <CalendarSearch className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-slate-800">
            Recupero d'emergenza da Google Calendar
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Se hai cancellato per errore gli appuntamenti di un giorno, puoi
            recuperarli leggendo il tuo Google Calendar (sync 2-way) e
            ricrearli in GHL. GCal conserva gli eventi cancellati per ~30 giorni.
          </p>
        </div>
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-medium text-slate-700">
            Data appuntamenti da recuperare
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid="gcal-recovery-date"
          />
        </div>
        <Button
          onClick={handleLoad}
          disabled={loading}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="gcal-recovery-load-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <CalendarSearch className="w-4 h-4 mr-2" />
          )}
          Carica eventi GCal
        </Button>
      </div>

      {events && (
        <div className="mt-5 border-t border-slate-100 pt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-slate-600">
              <strong>{events.length}</strong> eventi trovati su GCal per il{" "}
              {date}
              {events.length > 0 && (
                <>
                  {" · "}
                  <strong className="text-emerald-700">
                    {selected.size}
                  </strong>{" "}
                  selezionati (
                  <strong>{selectedRestorable.length}</strong> con contactId
                  valido)
                </>
              )}
            </div>
            {selectedRestorable.length > 0 && (
              <Button
                onClick={() => setConfirmOpen(true)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="gcal-recovery-restore-btn"
              >
                <CalendarPlus className="w-4 h-4 mr-2" />
                Ripristina {selectedRestorable.length} in GHL
              </Button>
            )}
          </div>

          {events.length === 0 ? (
            <div className="text-sm text-slate-500 italic py-4 text-center">
              Nessun evento su Google Calendar per questa data.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-2 w-8"></th>
                    <th className="py-2">Orario</th>
                    <th className="py-2">Paziente</th>
                    <th className="py-2">Titolo GCal</th>
                    <th className="py-2">Match GHL</th>
                    <th className="py-2">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => {
                    const isSel = selected.has(ev.gcal_event_id);
                    const m = ev.match;
                    return (
                      <tr
                        key={ev.gcal_event_id}
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`gcal-recovery-row-${ev.gcal_event_id}`}
                      >
                        <td className="py-2">
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleSelect(ev.gcal_event_id)}
                            disabled={!m?.contact_id}
                            data-testid={`gcal-recovery-check-${ev.gcal_event_id}`}
                          />
                        </td>
                        <td className="py-2 text-slate-700 font-mono text-xs">
                          {fmtTime(ev.start_iso)}
                        </td>
                        <td className="py-2 font-medium text-slate-800">
                          {ev.patient_name || (
                            <span className="text-slate-400 italic">
                              (nessuno)
                            </span>
                          )}
                        </td>
                        <td className="py-2 text-slate-600">
                          {ev.title || "—"}
                        </td>
                        <td className="py-2 text-xs">
                          {m?.contact_id ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              {m.match_type}
                            </span>
                          ) : m?.match_type === "ambiguous" ? (
                            <span className="inline-flex items-center gap-1 text-amber-700">
                              <AlertTriangle className="w-3.5 h-3.5" />
                              ambiguo (
                              {m.candidates?.length || 0})
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-700">
                              <XCircle className="w-3.5 h-3.5" />
                              non trovato
                            </span>
                          )}
                        </td>
                        <td className="py-2 text-xs text-slate-500">
                          {ev.gcal_status}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {result && (
        <div
          className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-md"
          data-testid="gcal-recovery-result"
        >
          <div className="text-sm font-medium">
            Risultato: <strong className="text-emerald-700">{result.created}</strong>{" "}
            creati, <strong className="text-red-700">{result.failed}</strong> falliti
            (su {result.total})
          </div>
          {result.details?.length > 0 && (
            <div className="mt-2 max-h-48 overflow-y-auto text-xs space-y-1">
              {result.details.map((d, i) => (
                <div
                  key={i}
                  className={
                    d.ok ? "text-emerald-700" : "text-red-700"
                  }
                >
                  {d.ok ? "✓" : "✗"} {d.title}
                  {!d.ok && d.error ? ` — ${d.error}` : ""}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="gcal-recovery-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              Ripristina {selectedRestorable.length} appuntamenti in GHL
            </DialogTitle>
            <DialogDescription>
              Verranno creati {selectedRestorable.length} nuovi appuntamenti nel
              calendario GHL selezionato. Questa azione è idempotente ma
              richiede la password master.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-700">
                Calendar ID di destinazione
              </label>
              <input
                type="text"
                value={calendarId}
                onChange={(e) => setCalendarId(e.target.value)}
                placeholder="es. nIdm8TdSb3B5pDcKEv7J (Calendario Generale)"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
                data-testid="gcal-recovery-calendar-id"
              />
              <p className="text-xs text-slate-500 mt-1">
                Puoi trovare l'ID del "Calendario Generale" in GHL → Settings →
                Calendars.
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">
                Password master
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid="gcal-recovery-password"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              data-testid="gcal-recovery-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              onClick={handleRestore}
              disabled={restoring || !password || !calendarId}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="gcal-recovery-confirm-btn"
            >
              {restoring ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CalendarPlus className="w-4 h-4 mr-2" />
              )}
              Conferma ripristino
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
